import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpacerMixabilityDto, SpacerService } from '../../../services/spacer.service';

interface MixabilityData {
  score: number | null;
  timeToWetSolids: number | null;
  notes: string;
}

export interface MixabilitySavedEvent {
  mixabilityId: number | null;
  score: number | null;
  timeToWetSolids: number | null;
  notes: string;
}

@Component({
  selector: 'app-mixability',
  imports: [CommonModule, FormsModule],
  templateUrl: './mixability.component.html',
  styleUrls: ['./mixability.component.css']
})
export class MixabilityComponent implements OnInit, OnChanges {
  @Input() spacerId: number | null = null;
  @Output() saved = new EventEmitter<MixabilitySavedEvent>();

  mixabilityData: MixabilityData = {
    score: null,
    timeToWetSolids: null,
    notes: ''
  };

  private mixabilityId: number | null = null;
  isSaving = false;
  mixabilityMessage = '';
  mixabilityError = '';

  // Score options matching the vortex scale 0-4
  scoreOptions: Array<{ value: number; label: string }> = [
    { value: 0, label: '0 - No Vortex' },
    { value: 1, label: '1 - Tight Vortex' },
    { value: 2, label: '2 - Small Vortex (1/4 inch)' },
    { value: 3, label: '3 - Medium Vortex (1/2 inch)' },
    { value: 4, label: '4 - Large Vortex (3/4 inch or more)' }
  ];

  constructor(private readonly spacerService: SpacerService) {}

  ngOnInit(): void {
    if ((this.spacerId ?? 0) > 0) {
      this.loadMixability();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['spacerId']) {
      return;
    }
    const current = Number(changes['spacerId'].currentValue);
    if (Number.isFinite(current) && current > 0) {
      this.loadMixability();
      return;
    }
    this.resetMixability();
  }

  private resetMixability(): void {
    this.mixabilityId = null;
    this.mixabilityData = {
      score: null,
      timeToWetSolids: null,
      notes: ''
    };
  }

  private emitSaved(): void {
    this.saved.emit({
      mixabilityId: this.mixabilityId,
      score: this.mixabilityData.score,
      timeToWetSolids: this.mixabilityData.timeToWetSolids,
      notes: this.mixabilityData.notes
    });
  }

  loadMixability(preserveMessage: boolean = false): void {
    if (!this.spacerId || this.spacerId <= 0) {
      this.resetMixability();
      return;
    }

    if (!preserveMessage) {
      this.mixabilityError = '';
      this.mixabilityMessage = '';
    }
    
    this.spacerService.getSpacerMixability(this.spacerId).subscribe({
      next: (mixability: SpacerMixabilityDto | null) => {
        if (!mixability) {
          this.resetMixability();
          this.emitSaved();
          return;
        }

        this.mixabilityId = mixability.id ?? null;
        this.mixabilityData = {
          score: mixability.score ?? null,
          timeToWetSolids: mixability.timeToWetSolids ?? null,
          notes: mixability.notes ?? ''
        };
        this.emitSaved();
      },
      error: (error) => {
        if (error?.status === 403) {
          this.mixabilityError = 'Access denied. You may not have permission to view this spacer mixability.';
        } else if (error?.status === 404) {
          // 404 might mean no mixability data exists yet - that's OK
          this.resetMixability();
        } else {
          this.mixabilityError = 'Failed to load mixability.';
        }
      }
    });
  }

  updateMixability(): void {
    if (!this.spacerId || this.spacerId <= 0 || this.isSaving) {
      return;
    }

    this.mixabilityError = '';
    this.mixabilityMessage = '';
    this.isSaving = true;

    const payload: SpacerMixabilityDto = {
      id: this.mixabilityId ?? undefined,
      slurryId: this.spacerId,
      score: this.mixabilityData.score,
      timeToWetSolids: this.mixabilityData.timeToWetSolids,
      notes: this.mixabilityData.notes?.trim() ? this.mixabilityData.notes.trim() : null
    };

    this.spacerService.updateSpacerMixability(this.spacerId, payload).subscribe({
      next: (result: SpacerMixabilityDto) => {
        this.isSaving = false;
        
        // Update local state from backend response
        this.mixabilityId = result.id ?? this.mixabilityId;
        this.mixabilityData = {
          score: result.score ?? null,
          timeToWetSolids: result.timeToWetSolids ?? null,
          notes: result.notes ?? ''
        };
        
        this.mixabilityMessage = 'Mixability updated successfully.';
        this.emitSaved();
        
        // Reload from backend to ensure data is in sync (preserve success message)
        setTimeout(() => {
          this.loadMixability(true);
        }, 1500);
      },
      error: (error) => {
        this.isSaving = false;
        
        // Extract error message from backend response
        let errorMessage = 'Failed to save mixability.';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.error?.title) {
          errorMessage = error.error.title;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.status === 403) {
          errorMessage = 'Access denied: You do not have permission to update this spacer mixability. The backend endpoint may not support mixability updates for spacers yet.';
        } else if (error?.status === 404) {
          errorMessage = 'Spacer not found or mixability endpoint does not exist for spacers.';
        } else if (error?.status === 405) {
          errorMessage = 'Method not allowed: The mixability endpoint may not support PUT requests for spacers.';
        }
        
        this.mixabilityError = errorMessage;
      }
    });
  }
}
